import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Phone, MapPin, Edit2, Upload, Banknote, AlertCircle,
  Activity, HandCoins, MessageSquare, Trash2, TrendingUp,
} from 'lucide-react';
import { OwnerLayout } from '../../../components/OwnerLayout';
import api from '../../../api';
import { encodeLan } from '../../../api';

function fmtINR(n) {
  const num = Number(n || 0);
  return `\u20B9${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtINRk(n) {
  const num = Number(n || 0);
  if (num >= 10000000) return `\u20B9${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `\u20B9${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `\u20B9${(num / 1000).toFixed(1)} K`;
  return `\u20B9${num.toFixed(0)}`;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
    });
  } catch { return String(d); }
}

const GRADE_COLOR = { A: '#15803d', B: '#65a30d', C: '#ca8a04', D: '#ea580c', E: '#b91c1c' };

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`} style={{ bottom: '24px' }}>{msg}</div>;
}

function bucketColor(bucket) {
  if (bucket === '120+') return '#7f1d1d';
  if (bucket === '91-120') return 'var(--red)';
  if (bucket === '61-90') return '#dc2626';
  if (bucket === '31-60') return '#ea580c';
  if (bucket === '1-30') return '#ca8a04';
  return 'var(--g4)';
}

export default function CustomerProfile() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, msg: '' });

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/recovery/customers/${encodeURIComponent(clientId)}/profile`);
      setData(res.data);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const uploadStatement = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/recovery/customers/${encodeURIComponent(clientId)}/statements/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(`Statement applied to ${res.data?.loanAccountNumber || 'loan'}`);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Statement upload failed');
    }
  };

  if (loading) {
    return (
      <OwnerLayout title="Customer Profile">
        <div className="panel" style={{ padding: 32, textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>
      </OwnerLayout>
    );
  }
  if (!data) {
    return (
      <OwnerLayout title="Customer Profile">
        <div className="panel" style={{ padding: 32, textAlign: 'center', color: 'var(--g3)' }}>
          Customer not found.
          <div style={{ marginTop: 12 }}>
            <button className="btn-outline" onClick={() => navigate('/owner/recovery/customers')}>
              <ArrowLeft size={14} style={{ marginRight: 4 }} /> Back to Customers
            </button>
          </div>
        </div>
      </OwnerLayout>
    );
  }

  const { customer, branch, loans, stats, creditScore, recent } = data;

  return (
    <OwnerLayout title={`Customer · ${customer.fullName}`}>
      <Toast {...toast} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button className="btn-outline" onClick={() => navigate(-1)}>
          <ArrowLeft size={14} style={{ marginRight: 4 }} /> Back
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <label className="btn-outline" style={{ cursor: 'pointer' }} title="Upload CBS loan statement PDF for this customer">
            <Upload size={14} style={{ marginRight: 4 }} /> Upload statement
            <input
              type="file" accept=".pdf,application/pdf" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadStatement(f); e.target.value = ''; }}
            />
          </label>
          <button className="btn-outline" onClick={() => navigate(`/owner/recovery/customers?edit=${encodeURIComponent(customer.clientId)}`)}>
            <Edit2 size={14} style={{ marginRight: 4 }} /> Edit details
          </button>
        </div>
      </div>

      {/* Header card: identity + credit score */}
      <div className="panel" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ flex: '2 1 320px', minWidth: 280 }}>
            <h2 className="font-heading font-bold text-navy" style={{ fontSize: 22, margin: 0 }}>
              {customer.fullName}
              {customer.needsStatementUpload && (
                <span title="Statement upload needed" style={{ marginLeft: 8, color: '#a16207' }}>
                  <AlertCircle size={16} style={{ verticalAlign: 'middle' }} />
                </span>
              )}
            </h2>
            <div style={{ marginTop: 4, fontSize: 13, color: 'var(--g3)' }}>
              Client ID: <strong style={{ color: 'var(--navy)' }}>{customer.clientId}</strong>
              {branch && <span> · Branch: <strong>{branch.branchName || '—'}</strong></span>}
            </div>
            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, fontSize: 13 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Phone size={13} style={{ color: 'var(--g3)' }} />
                <span><strong>{customer.primaryPhone || '—'}</strong>{customer.alternatePhone ? ` · ${customer.alternatePhone}` : ''}</span>
              </div>
              {customer.address && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <MapPin size={13} style={{ color: 'var(--g3)', marginTop: 2 }} />
                  <span>{customer.address}</span>
                </div>
              )}
              {customer.guarantorName && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ color: 'var(--g3)' }}>Guarantor:</span>
                  <span>{customer.guarantorName}{customer.guarantorPhone ? ` · ${customer.guarantorPhone}` : ''}</span>
                </div>
              )}
            </div>
          </div>

          {/* Credit score block */}
          <div style={{ flex: '1 1 220px', minWidth: 200, background: '#f8fafc', border: '1px solid var(--g2)', borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--g3)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
              <TrendingUp size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Internal Credit Score
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
              <div style={{ fontSize: 34, fontWeight: 700, color: GRADE_COLOR[creditScore.grade] || 'var(--navy)' }}>
                {creditScore.score}
              </div>
              <div style={{ fontSize: 20, fontWeight: 600, color: GRADE_COLOR[creditScore.grade] || 'var(--g3)' }}>
                {creditScore.grade}
              </div>
              <div style={{ fontSize: 11, color: 'var(--g3)' }}>/ 100</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--g3)', marginTop: 6 }}>{creditScore.rationale}</div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="od-stats" style={{ marginBottom: 12 }}>
        <div className="od-stat">
          <div className="od-stat-lbl">Active Loans</div>
          <div className="od-stat-val">{stats.activeLoans} / {stats.totalLoans}</div>
          <div className="od-stat-sub">Total {stats.totalLoans}</div>
        </div>
        <div className="od-stat accent">
          <div className="od-stat-lbl">Total Outstanding</div>
          <div className="od-stat-val">{fmtINRk(stats.totalOutstanding)}</div>
          <div className="od-stat-sub">{fmtINR(stats.totalOutstanding)}</div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl">Total Overdue</div>
          <div className="od-stat-val red">{fmtINRk(stats.totalOverdue)}</div>
          <div className="od-stat-sub">Max DPD {stats.maxDpdDays}d</div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl">Bounced / Broken PTPs</div>
          <div className="od-stat-val">{stats.promisesBroken}</div>
          <div className="od-stat-sub">of {stats.promisesCount} promised · {stats.promisesKept} kept</div>
        </div>
      </div>

      {/* Loans table */}
      <div className="panel" style={{ padding: 0, overflow: 'hidden', marginBottom: 12 }}>
        <div className="panel-header" style={{ padding: '10px 12px' }}>
          <span className="panel-title"><Banknote size={14} /> Loan Accounts ({loans.length})</span>
        </div>
        {loans.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--g3)', fontSize: 13 }}>No loans on file.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)', textAlign: 'left' }}>
                  <th style={th}>Loan #</th>
                  <th style={th}>Product</th>
                  <th style={thR}>EMI</th>
                  <th style={thR}>Outstanding</th>
                  <th style={thR}>Overdue</th>
                  <th style={th}>DPD</th>
                  <th style={th}>Next due</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {loans.map((l) => (
                  <tr key={l.id} style={{ borderBottom: '1px solid var(--g2)', cursor: 'pointer' }}
                      onClick={() => navigate(`/owner/recovery/cases/${encodeLan(l.loanAccountNumber)}`)}>
                    <td style={td}>
                      <strong>{l.loanAccountNumber}</strong>
                      {l.needsStatementUpload && <AlertCircle size={11} style={{ marginLeft: 4, color: '#a16207', verticalAlign: 'middle' }} />}
                    </td>
                    <td style={td}>{l.productType || '—'}{l.glCode ? <span style={{ color: 'var(--g3)' }}> · GL {l.glCode}</span> : null}</td>
                    <td style={tdR}>{fmtINR(l.emiAmount)}</td>
                    <td style={tdR}>{fmtINR(l.outstandingAmount)}</td>
                    <td style={tdR}><strong style={{ color: 'var(--red)' }}>{fmtINR(l.totalOverdueAmount)}</strong></td>
                    <td style={td}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: 'white', background: bucketColor(l.dpdBucket) }}>
                        {l.dpdDays ?? 0}d · {l.dpdBucket || '0'}
                      </span>
                    </td>
                    <td style={td}>{l.nextDueDate || '—'}</td>
                    <td style={td}>
                      <span className="agent-view-link">Open case →</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Activity feed */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-title"><Activity size={14} /> Recent Activity ({recent.length})</span>
        </div>
        {recent.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--g3)', fontSize: 13 }}>No recovery activity yet.</div>
        ) : (
          <div style={{ padding: '6px 0' }}>
            {recent.map((r, i) => {
              const Icon = r.kind === 'payment' ? HandCoins : r.kind === 'promise' ? AlertCircle : MessageSquare;
              const color = r.kind === 'payment' ? '#15803d' : r.kind === 'promise' ? '#a16207' : 'var(--navy)';
              return (
                <div key={i} className="od-feed-item" style={{ cursor: 'pointer' }}
                     onClick={() => navigate(`/owner/recovery/cases/${encodeLan(r.loanAccountNumber)}`)}>
                  <span style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: 11, background: `${color}15`, color, alignItems: 'center', justifyContent: 'center', marginRight: 8 }}>
                    <Icon size={12} />
                  </span>
                  <div className="od-feed-info" style={{ flex: 1 }}>
                    <div className="od-feed-text">
                      {r.summary}
                      <span style={{ color: 'var(--g3)', fontWeight: 400 }}> · {r.loanAccountNumber}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--g3)' }}>{fmtDate(r.when)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </OwnerLayout>
  );
}

const th = { padding: '8px 10px', fontWeight: 600, color: '#374151', fontSize: 12 };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const tdR = { ...td, textAlign: 'right' };
